<template>
  <div class="enjoy-cooking">
    <AppHeader :email="user?.email || null" :hat="store.state.hat" @sign-out="leave"/>
    <UpdateAvailableBanner/>

    <div v-if="store.state.appError" class="app-error" role="alert">
      <span>{{ store.state.appError }}</span>
      <button type="button" class="btn btn-sm" @click="setAppError(null)">Dismiss</button>
    </div>

    <!-- Nothing renders until Firebase has answered "is anyone signed in?"
         and the hat is known. Rendering earlier flashes the sign-in screen
         at someone who never signed out. -->
    <main v-if="!settled" class="enjoy-cooking__settling"/>
    <SignIn v-else-if="!user"/>
    <main v-else class="enjoy-cooking__content">
      <router-view :hat="store.state.hat"/>
    </main>

    <BugReportButton/>
    <BugResolutionNotice/>
    <p class="enjoy-cooking__stamp" role="button" title="Tap to reload" @click="forceRefresh">{{ stamp }}</p>
  </div>
</template>

<script>
// The shell: the session, the hat, and the house auto-update machinery —
// the same code Movie Requests, Movie Hat and Cinema Roll carry, because it
// is the hard-won version.
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppHeader from './components/AppHeader.vue';
import SignIn from './components/SignIn.vue';
import UpdateAvailableBanner from './components/UpdateAvailableBanner.vue';
import BugReportButton from './components/BugReportButton.vue';
import BugResolutionNotice from './components/BugResolutionNotice.vue';
import { reloadForUpdate, isSafeMomentForReload, shouldAutoAttempt } from './utils/appUpdate.js';
import { flushStashedBugReports } from './utils/bugReports.js';
import { buildStampText, forceRefresh } from './utils/buildStamp.js';
import { resolveHat } from './utils/hat.js';
import { user, authSettled, signOut } from './firebase.js';
import { store, setAppError, setEmail, setHat } from './state.js';

export default {
  name: 'EnjoyCooking',
  components: { AppHeader, SignIn, UpdateAvailableBanner, BugReportButton, BugResolutionNotice },
  setup () {
    const route = useRoute();
    const router = useRouter();
    const hatReady = ref(false);
    const settled = computed(() => authSettled.value && (!user.value || hatReady.value));

    const stamp = buildStampText({
      version: process.env.VUE_APP_VERSION,
      buildTime: process.env.VUE_APP_BUILD_TIME
    });

    watch(() => user.value?.uid, async () => {
      setEmail(user.value?.email || null);
      hatReady.value = false;
      setHat(null);
      if (!user.value) { hatReady.value = true; return; }
      try {
        setHat(await resolveHat(user.value.email));
      } catch {
        setAppError('Could not find your Meal Hat. Is this the account you use there?');
      } finally {
        hatReady.value = true;
      }
    }, { immediate: true });

    async function leave () {
      await signOut();
      router.replace('/');
    }

    // --- the house auto-update machinery ------------------------------------
    let lastActivityAt = Date.now();
    let lastBecameVisibleAt = Date.now();
    let deployedBundleSeen = null;
    let autoUpdateTimer = null;

    function currentBundleName () {
      for (const script of document.querySelectorAll('script[src]')) {
        const match = (script.getAttribute('src') || '').match(/js\/app\.[a-z0-9]+\.js/);
        if (match) return match[0];
      }
      return null;
    }

    async function checkDeployedBundle () {
      if (store.state.updateAvailable) return;
      const running = currentBundleName();
      if (!running) return;
      try {
        const response = await fetch(`${process.env.BASE_URL || '/'}index.html?updateCheck=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;
        const deployed = (await response.text()).match(/js\/app\.[a-z0-9]+\.js/);
        if (deployed && deployed[0] !== running) {
          deployedBundleSeen = deployed[0];
          store.state.updateAvailable = true;
          armAutoUpdate();
        }
      } catch {
        // Offline, blocked, or the check simply failed — next trigger.
      }
    }

    async function checkForServiceWorkerUpdate () {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) await registration.update();
        } catch { /* best effort */ }
      }
      await checkDeployedBundle();
    }

    function armAutoUpdate () {
      const target = deployedBundleSeen || 'unknown';
      if (!shouldAutoAttempt(target)) return;
      const fresh = Date.now() - lastBecameVisibleAt < 5000;
      if (fresh && isSafeMomentForReload({ routePath: route.path || '' })) {
        reloadForUpdate();
        return;
      }
      if (autoUpdateTimer) clearInterval(autoUpdateTimer);
      autoUpdateTimer = setInterval(() => {
        const quiet = Date.now() - lastActivityAt > 25000;
        if (quiet && isSafeMomentForReload({ routePath: route.path || '' })) {
          clearInterval(autoUpdateTimer);
          autoUpdateTimer = null;
          reloadForUpdate();
        }
      }, 5000);
    }

    onMounted(() => {
      const onForeground = () => {
        lastBecameVisibleAt = Date.now();
        checkForServiceWorkerUpdate();
      };
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') onForeground(); });
      window.addEventListener('pageshow', onForeground);
      window.addEventListener('focus', onForeground);
      ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach((eventName) => {
        window.addEventListener(eventName, () => { lastActivityAt = Date.now(); }, { passive: true });
      });
      setInterval(checkForServiceWorkerUpdate, 30 * 60 * 1000);
      flushStashedBugReports().catch(() => {});
    });

    return { store, user, settled, stamp, leave, setAppError, forceRefresh };
  }
};
</script>

<style>
.enjoy-cooking { min-height: 100vh; display: flex; flex-direction: column; }
.enjoy-cooking__content { flex: 1; }
.enjoy-cooking__settling { min-height: 50vh; }
.enjoy-cooking__stamp { color: var(--muted); opacity: 0.6; font-size: 0.65rem; margin: 0; padding: 0 1rem calc(var(--safe-bottom) + 0.75rem); text-align: center; }
.app-error { display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; margin: 0.75rem 1.25rem 0; padding: 0.6rem 0.9rem; border-radius: var(--radius); background: #fff3cd; color: #5c4400; }
</style>
